import React from 'react';
import { Link } from 'react-router-dom';

const LightingProductCard = ({ product }) => {
  const [selectedSize, setSelectedSize] = React.useState('XS'); // Default to XS
  
  // Size options with prices
  const sizeOptions = [
    { size: 'XS', price: 440, label: 'XS' },
    { size: 'S', price: 540, label: 'S' },
    { size: 'M', price: 690, label: 'M' },
    { size: 'L', price: 840, label: 'L' }
  ];

  // Get current price based on selected size
  const getCurrentPrice = () => {
    const sizeOption = sizeOptions.find(opt => opt.size === selectedSize);
    return sizeOption ? sizeOption.price : 440; // Default to XS price
  };

  // Format price - handle both string and number formats
  const formatPrice = (price) => {
    if (typeof price === 'number') {
      return `CHF ${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    if (typeof price === 'string') {
      // Extract number from string like "$1800" or "CHF 1800"
      const match = price.match(/[\d,.]+/);
      if (match) {
        const number = parseFloat(match[0].replace(',', ''));
        return `CHF ${number.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
      return price;
    }
    return `CHF ${(price / 100).toFixed(2)}`;
  };

  // Get image source based on selected size (if sizes data exists)
  const getImageSrc = () => {
    if (!product.images || product.images.length === 0) return '/placeholder-lighting.png';
    
    // Check if product has size-based image mapping
    if (product.sizes && product.sizes[selectedSize] && product.sizes[selectedSize].imageIndex !== undefined) {
      const imageIndex = product.sizes[selectedSize].imageIndex;
      if (product.images[imageIndex]) {
        const img = product.images[imageIndex];
        return img.startsWith('http') || img.startsWith('/') ? img : `/${img}`;
      }
    }
    
    // Fallback to first image
    const img = product.images[0];
    return img.startsWith('http') || img.startsWith('/') ? img : `/${img}`;
  };

  return (
    <Link
      to={`/product/${product.id}`}
      className="group relative overflow-hidden bg-white rounded-lg shadow-lg hover:shadow-2xl transition-all duration-500"
    >
      {/* Image Container */}
      <div className="relative aspect-[3/4] overflow-hidden bg-gray-100">
        <img
          src={getImageSrc()}
          alt={product.name}
          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
        />
        
        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-500" />
        
        {/* Availability Badge */}
        {!product.available && (
          <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold">
            SOLD OUT
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[hsl(45,100%,70%)] transition-colors duration-300 h-14 flex items-start">
          <span className="line-clamp-2">{product.name}</span>
        </h3>
        
        <p className="text-gray-600 text-sm mb-3 line-clamp-2 h-10">
          {product.description}
        </p>
        
        {/* Size Selector */}
        <div className="mb-4">
          <p className="text-xs text-gray-500 mb-2 font-medium">Select Size:</p>
          <div className="grid grid-cols-4 gap-2">
            {sizeOptions.map((option) => {
              const isSizeSold = product.sizes && product.sizes[option.size]?.sold;
              return (
                <button
                  key={option.size}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!isSizeSold) {
                      setSelectedSize(option.size);
                    }
                  }}
                  disabled={isSizeSold}
                  className={`relative px-2 py-2 text-xs font-bold rounded-lg border-2 transition-all duration-200 ${
                    isSizeSold
                      ? 'opacity-50 cursor-not-allowed border-gray-300 bg-gray-200 text-gray-400'
                      : selectedSize === option.size
                      ? 'border-[hsl(45,100%,70%)] bg-[hsl(45,100%,70%)] text-black'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-[hsl(45,100%,70%)]'
                  }`}
                >
                  {option.label}
                  {isSizeSold && (
                    <span className="absolute top-0 right-0 -mt-1 -mr-1 text-[8px] bg-red-500 text-white px-1 rounded">
                      SOLD
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Price */}
        <div className="mb-3 text-center">
          <div className="text-sm text-gray-500 mb-1">starting from</div>
          <div className="text-2xl font-bold text-gray-900">
            {formatPrice(getCurrentPrice())}
          </div>
        </div>
        
        {/* View Details */}
        <div className="text-center">
          <span className="text-[hsl(45,100%,70%)] font-semibold group-hover:translate-x-2 transition-transform duration-300 inline-block">
            View Details →
          </span>
        </div>
      </div>
    </Link>
  );
};

export default LightingProductCard;
